import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { TiktokShopProvider, useTiktokShop } from '@/tiktok-platform/context/TiktokShopContext';
import { TiktokSyncJobProvider } from '@/tiktok-platform/context/TiktokSyncJobContext';
import TiktokShopTopBar from '@/tiktok-platform/layout/TiktokShopTopBar';
import TiktokGlobalSyncProgress from '@/tiktok-platform/components/TiktokGlobalSyncProgress';
import TiktokNoticeDrawerHost from '@/tiktok-platform/components/TiktokNoticeDrawerHost';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import {
  IPE_TIKTOK_BASE_PATH,
  TIKTOK_SHOP_SWITCH_TOAST_KEY,
  buildTiktokChildPath,
  ensureTiktokWorkspaceTokenShared,
  navItemsForShopTypeSlug,
  openTiktokPathInNewTab,
  parseTiktokShopTypeSlugFromPath,
} from '@/tiktok-platform/constants/tiktokRoutes';
import { isAllShopsScope, isConcreteShopId } from '@/utils/platformShopScope';
import { showToast } from '@/utils/toast';
import { platformNavLabel } from '@/i18n/platformNavLabel';
import '@/styles/tiktok-platform/tiktok-platform.css';

const NAV_EXPAND_STORAGE_KEY = 'ipe_tiktok_nav_expanded';

function appendShopIdQuery(path, shopId) {
  const base = String(path || '');
  if (!isConcreteShopId(shopId)) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}shopId=${encodeURIComponent(String(shopId))}`;
}

function isNavGroupActive(pathname, parentPath) {
  const slug = parseTiktokShopTypeSlugFromPath(pathname);
  const base = slug
    ? `${IPE_TIKTOK_BASE_PATH}/${slug}/${parentPath}`
    : `${IPE_TIKTOK_BASE_PATH}/${parentPath}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

function collectActiveGroupIds(pathname) {
  const slug = parseTiktokShopTypeSlugFromPath(pathname) || 'semi';
  return navItemsForShopTypeSlug(slug)
    .filter((item) => Array.isArray(item.children) && item.children.length > 0)
    .filter((item) => isNavGroupActive(pathname, item.path))
    .map((item) => item.id);
}

function readStoredExpanded() {
  try {
    const raw = sessionStorage.getItem(NAV_EXPAND_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : null;
  } catch {
    return null;
  }
}

function writeStoredExpanded(ids) {
  try {
    sessionStorage.setItem(NAV_EXPAND_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function TiktokSidebarNav({ expandedIds, toggleGroup, pathname }) {
  const { t } = useTranslation('workbench');
  const navText = (item) => platformNavLabel(t, item);
  const slug = parseTiktokShopTypeSlugFromPath(pathname) || 'semi';
  const navItems = useMemo(() => navItemsForShopTypeSlug(slug), [slug]);
  const { activeShopId, scopedShops = [], isAllShopsMode } = useTiktokShop();
  const [pickOpen, setPickOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState('');

  const interceptComingSoon = (e, label) => {
    e.preventDefault();
    e.stopPropagation();
    showToast(t('platformNav.comingSoon', { label }), 'info');
  };

  const openNewTabPath = (to) => {
    if (isAllShopsMode || isAllShopsScope(activeShopId)) {
      setPendingPath(to);
      setPickOpen(true);
      return;
    }
    const concrete = isConcreteShopId(activeShopId) ? activeShopId : '';
    openTiktokPathInNewTab(appendShopIdQuery(to, concrete));
  };

  return (
    <>
    <nav className="tiktok-platform__sidebar" aria-label={t('platformNav.menuTiktok')}>
      {navItems.map((item) => {
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        if (!hasChildren) {
          if (item.comingSoon) {
            return (
              <a
                key={item.id}
                href={buildTiktokChildPath(item.path, '')}
                className="tiktok-platform__nav-item tiktok-platform__nav-item--soon"
                aria-disabled="true"
                onClick={(e) => interceptComingSoon(e, navText(item))}
              >
                {navText(item)}
              </a>
            );
          }
          return (
            <NavLink
              key={item.id}
              to={item.path}
              className={({ isActive }) =>
                `tiktok-platform__nav-item${isActive ? ' tiktok-platform__nav-item--active' : ''}`
              }
            >
              {navText(item)}
            </NavLink>
          );
        }

        const routeActive = isNavGroupActive(pathname, item.path);
        const expanded = expandedIds.includes(item.id);
        const panelId = `tiktok-nav-panel-${item.id}`;

        return (
          <div
            key={item.id}
            className={`tiktok-platform__nav-group${expanded ? ' tiktok-platform__nav-group--open' : ''}${routeActive ? ' tiktok-platform__nav-group--active' : ''}`}
          >
            <button
              type="button"
              className={`tiktok-platform__nav-group-toggle${routeActive ? ' is-active' : ''}`}
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => toggleGroup(item.id)}
            >
              <span className="tiktok-platform__nav-group-label">{navText(item)}</span>
              <ChevronDown
                size={14}
                strokeWidth={2.25}
                className={`tiktok-platform__nav-group-chevron${expanded ? ' is-open' : ''}`}
                aria-hidden="true"
              />
            </button>
            <div
              id={panelId}
              className="tiktok-platform__nav-children"
              role="group"
              aria-label={t('platformNav.submenu', { label: navText(item) })}
              hidden={!expanded}
            >
              {item.children.map((child) => {
                const to = buildTiktokChildPath(item.path, child.path);
                const className = 'tiktok-platform__nav-item tiktok-platform__nav-item--child';
                const soon = Boolean(item.comingSoon || child.comingSoon);
                if (soon) {
                  return (
                    <a
                      key={child.id}
                      href={to}
                      className={`${className} tiktok-platform__nav-item--soon`}
                      aria-disabled="true"
                      onClick={(e) => interceptComingSoon(e, navText(child) || navText(item))}
                    >
                      {navText(child)}
                    </a>
                  );
                }
                if (child.openInNewTab) {
                  return (
                    <a
                      key={child.id}
                      href={to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={className}
                      onClick={(e) => {
                        e.preventDefault();
                        openNewTabPath(to);
                      }}
                    >
                      {navText(child)}
                    </a>
                  );
                }
                return (
                  <NavLink
                    key={child.id}
                    to={to}
                    end={child.path === '' || child.id === 'info' || child.id === 'reviews'}
                    className={({ isActive }) =>
                      `${className}${isActive ? ' tiktok-platform__nav-item--active' : ''}`
                    }
                  >
                    {navText(child)}
                  </NavLink>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
    <PlatformShopPickModal
      open={pickOpen}
      shops={scopedShops}
      title={t('platformBar.pickPublishShop')}
      description={t('platformBar.pickPublishShopDesc')}
      confirmText={t('platformBar.openPublishPage')}
      onClose={() => {
        setPickOpen(false);
        setPendingPath('');
      }}
      onConfirm={(shopId) => {
        setPickOpen(false);
        const path = pendingPath;
        setPendingPath('');
        if (path) openTiktokPathInNewTab(appendShopIdQuery(path, shopId));
      }}
    />
    </>
  );
}

export default function TiktokPlatformLayout() {
  const location = useLocation();
  const hideWorkspaceChrome = isNavGroupActive(location.pathname, 'products')
    || isNavGroupActive(location.pathname, 'templates')
    || isNavGroupActive(location.pathname, 'orders')
    || isNavGroupActive(location.pathname, 'after-sales')
    || isNavGroupActive(location.pathname, 'finance')
    || isNavGroupActive(location.pathname, 'shop')
    || isNavGroupActive(location.pathname, 'home')
    || isNavGroupActive(location.pathname, 'compliance')
    || isNavGroupActive(location.pathname, 'stock')
    || isNavGroupActive(location.pathname, 'marketing')
    || isNavGroupActive(location.pathname, 'analytics')
    || isNavGroupActive(location.pathname, 'fulfillment')
    || location.pathname.startsWith(`${IPE_TIKTOK_BASE_PATH}/certificates`);

  const activeGroupIds = useMemo(
    () => collectActiveGroupIds(location.pathname),
    [location.pathname],
  );

  const [expandedIds, setExpandedIds] = useState(() => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const stored = readStoredExpanded();
    const active = collectActiveGroupIds(pathname);
    if (stored) {
      return Array.from(new Set([...stored, ...active]));
    }
    const slug = parseTiktokShopTypeSlugFromPath(pathname) || 'semi';
    return active.length > 0
      ? active
      : navItemsForShopTypeSlug(slug).filter((item) => item.children?.length).map((item) => item.id);
  });

  useEffect(() => {
    ensureTiktokWorkspaceTokenShared();
  }, []);

  useEffect(() => {
    if (activeGroupIds.length === 0) return;
    setExpandedIds((prev) => {
      const next = Array.from(new Set([...prev, ...activeGroupIds]));
      if (next.length === prev.length && next.every((id) => prev.includes(id))) {
        return prev;
      }
      writeStoredExpanded(next);
      return next;
    });
  }, [activeGroupIds]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(TIKTOK_SHOP_SWITCH_TOAST_KEY);
      if (!raw) return;
      sessionStorage.removeItem(TIKTOK_SHOP_SWITCH_TOAST_KEY);
      const payload = JSON.parse(raw);
      if (payload?.message) {
        showToast(payload.message, payload.type || 'success');
      }
    } catch {
      sessionStorage.removeItem(TIKTOK_SHOP_SWITCH_TOAST_KEY);
    }
  }, []);

  const toggleGroup = (id) => {
    setExpandedIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id];
      writeStoredExpanded(next);
      return next;
    });
  };

  return (
    <TiktokShopProvider>
      <TiktokSyncJobProvider>
        <div className={`tiktok-platform${hideWorkspaceChrome ? ' tiktok-platform--product-ops' : ''}`} data-testid="tiktok-platform-layout">
          <TiktokShopTopBar />
          <TiktokNoticeDrawerHost />
          <div className="tiktok-platform__body">
            <TiktokSidebarNav
              expandedIds={expandedIds}
              toggleGroup={toggleGroup}
              pathname={location.pathname}
            />
            <main className={`tiktok-platform__main${hideWorkspaceChrome ? ' tiktok-platform__main--product-ops' : ''}`}>
              <TiktokGlobalSyncProgress />
              <Outlet />
            </main>
          </div>
        </div>
      </TiktokSyncJobProvider>
    </TiktokShopProvider>
  );
}
